import React, { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material"
import LoadingButton from "@mui/lab/LoadingButton"
import LockOutlined from "@mui/icons-material/LockOutlined"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import StorageService from "../../../base/services/StorageService"
import base64url from "base64url"
import { JSEncrypt } from "jsencrypt"
import AuthApi from "../../../base/services/AuthApi"

const publicKey = `
      -----BEGIN PUBLIC KEY-----
      MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsy+1EQqpdOCQM4OaAflU9rQZGzo8TexVbkez7q67egtwZT5BCG5bClBk95paacF3HgMwh4se6ZhttdkiJfr/gGagOqP2FTZKqEaz7d24FSpYyKUVdgZ7eDNgiBbkWVZi83dGqlMGM2wmES3Q614P3JN+BiZ5srvc4q0BXnw8y40MB10KY0dONVuKrywKEKoHCfjIvKJNH3FyYaCwslQC4m94ErDr5OGbO3qPLiJzw7fRoU3WN05OZvGuYkgS0yN2KUKOp/vAyG6h1Mf+bayILTAGJRxyYdLGvL8Y/90NIFNvXtO4IHfY8HO/bymOic/gPE7XU1lHTK1EvrF55a/E3wIDAQAB
      -----END PUBLIC KEY-----`

const PasswordConfirmModal = ({ open, isSubmit, handleAccept, onClose, operationLabel = "Terminal operation", terminalSerial, packageName, acceptDisabled = false }) => {
  const [password, setPassword] = useState("")
  const [visible, setVisible] = useState(false)
  const [verified, setVerified] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const request = useRef(0)
  const pending = useRef(false)

  useEffect(() => {
    setPassword("")
    setVisible(false)
    setVerified(false)
    setLoading(false)
    setError("")
    pending.current = false
    return () => { request.current += 1 }
  }, [open, operationLabel, terminalSerial, packageName])

  const verifyPassword = async () => {
    if (!password || pending.current || isSubmit || verified) return
    const requestId = ++request.current
    pending.current = true
    setLoading(true)
    setError("")
    try {
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(publicKey)
      const encrypted = encrypt.encrypt(password)
      if (!encrypted) throw new Error("Encryption failed")
      const response = await AuthApi.get("/users/confirm/" + StorageService.getUsername(), {
        headers: { "Content-Type": "application/json", password: base64url.toBase64(encrypted) },
      })
      if (requestId !== request.current) return
      if (response.data === true) {
        setVerified(true)
        setPassword("")
        setVisible(false)
      } else {
        setError("Incorrect password. Please try again.")
      }
    } catch (err) {
      if (requestId === request.current) setError("Unable to verify your password. Please try again.")
    } finally {
      if (requestId === request.current) {
        pending.current = false
        setLoading(false)
      }
    }
  }

  const close = () => {
    if (isSubmit) return
    request.current += 1
    onClose()
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="password-confirm-title" aria-describedby="password-confirm-description" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle id="password-confirm-title" sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: "flex", p: 1.25, bgcolor: "#eaf5f4", color: "#116c6f", borderRadius: 2 }}><LockOutlined /></Box>
          <Box><Typography component="span" variant="h6" fontWeight={600}>Confirm terminal operation</Typography>
            <Typography variant="body2" color="text.secondary">Password verification required</Typography></Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5}>
          <Box sx={{ p: 2, bgcolor: "#f5f8fa", border: "1px solid #dce7e7", borderRadius: 2, overflowWrap: "anywhere" }}>
            <Typography fontWeight={600}>{operationLabel}</Typography>
            <Typography variant="body2" color="text.secondary">Terminal: {terminalSerial || "Not available"}</Typography>
            {packageName && <Typography variant="body2" color="text.secondary">Application: {packageName}</Typography>}
          </Box>
          <Typography id="password-confirm-description" variant="body2" color="text.secondary">Verify your account password, then confirm to send this operation to the terminal.</Typography>
          {verified ? <Alert severity="success">Password verified. Review the details above and confirm when ready.</Alert> : <>
            <TextField autoFocus fullWidth label="Account password" type={visible ? "text" : "password"} value={password} autoComplete="current-password" disabled={loading || isSubmit}
              error={!!error} helperText={error || "Enter the password you use to sign in."}
              onChange={(event) => { setPassword(event.target.value); setVerified(false); setError("") }}
              onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); verifyPassword() } }}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={visible ? "Hide password" : "Show password"} disabled={loading || isSubmit} onClick={() => setVisible(!visible)} edge="end">{visible ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
            <LoadingButton loading={loading} disabled={!password || isSubmit} onClick={verifyPassword} variant="outlined" sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, textTransform: "none" }}>Verify password</LoadingButton>
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #edf1f3", gap: 1, flexWrap: "wrap" }}>
        <Button disabled={isSubmit} onClick={close} sx={{ textTransform: "none" }}>Cancel</Button>
        <LoadingButton loading={isSubmit} disabled={!verified || loading || acceptDisabled || isSubmit} variant="contained" color="error" onClick={() => { if (verified && !loading && !isSubmit && !acceptDisabled) handleAccept() }} sx={{ textTransform: "none" }}>Confirm operation</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default PasswordConfirmModal
